import { supabase } from '../lib/supabase';
import { JerseyColor, Player } from '../types/Player';
import { PlayerEvaluation } from '../types/playerEvaluation';

export async function fetchPlayersForSession(sessionId: string): Promise<Player[]> {
  const { data: checkinData, error: checkinError } = await supabase
    .from('checkins')
    .select(`
      jersey_number,
      jersey_color,
      players:players (
        id,
        first_name,
        last_name,
        sex,
        birthdate,
        age_group_id,
        profile
      )
    `)
    .eq('session_id', sessionId);

  if (checkinError) {
    console.error('Error fetching checkins/players:', checkinError);
    return [];
  }
  if (!checkinData) {
    return [];
  }

  const { data: evaluationData, error: evaluationError } = await supabase
    .rpc('get_eval_summary_for_session', { session_id_param: sessionId })
    .select('player_id, coach_id');

  if (evaluationError) {
    console.error('Error fetching evaluations for session:', evaluationError);
  }

  const evaluationsByPlayer = new Map<string, string[]>();
  if (evaluationData) {
    for (const evaluation of evaluationData) {
      const coachIds = evaluationsByPlayer.get(evaluation.player_id) || [];
      if (!coachIds.includes(evaluation.coach_id)) {
        coachIds.push(evaluation.coach_id);
      }
      evaluationsByPlayer.set(evaluation.player_id, coachIds);
    }
  }

  return (checkinData as any[]).map(record => {
    const p = record.players;
    const evaluatedByCoachIds = evaluationsByPlayer.get(p.id) || [];

    return {
      id: p.id,
      first_name: p.first_name,
      last_name: p.last_name,
      birthdate: p.birthdate,
      sex: p.sex,
      age_group_id: p.age_group_id,
      profile: p.profile,
      jerseyNumber: record.jersey_number,
      jerseyColor: record.jersey_color as JerseyColor | undefined,
      evaluatedByCoachIds: evaluatedByCoachIds
    };
  });
}

export async function fetchEvaluationsForPlayer(playerId: string): Promise<PlayerEvaluation[]> {
  const { data, error } = await supabase
    .from('player_evaluations')
    .select('*')
    .eq('player_id', playerId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error(`Error fetching evaluations for player ${playerId}:`, error);
    return [];
  }

  return (data || []).map(evaluation => ({
    ...evaluation,
    evaluation_session_id: evaluation.evaluation_id || evaluation.evaluation_session_id,
  }));
}

export async function updateJersey(
  playerId: string,
  sessionId: string,
  newJerseyNumber: string,
  newJerseyColor: JerseyColor
): Promise<void> {
  const { error } = await supabase
    .from('checkins')
    .update({ jersey_number: newJerseyNumber, jersey_color: newJerseyColor })
    .eq('player_id', playerId)
    .eq('session_id', sessionId);

  if (error) {
    console.error('Error updating jersey:', error);
    throw error;
  }
}